import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Package, Container, Droplets } from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.55, ease: "easeOut" },
};

const GRADES = [
  {
    name: "Penetration Grade",
    icon: Droplets,
    items: ["Bitumen 60/70", "Bitumen 80/100"],
    text: "The workhorse grades for hot-mix asphalt and base courses in tropical and temperate climates.",
  },
  {
    name: "Cutback Bitumen",
    icon: Package,
    items: ["MC-30", "MC-70"],
    text: "Medium-curing cutbacks for prime coats and cold-mix applications where heating is impractical.",
  },
  {
    name: "Bitumen Emulsions",
    icon: Container,
    items: ["CRS / CSS grades", "Supplied on request"],
    text: "Cationic emulsions for surface dressing, tack coats and slurry works, quoted against project specs.",
  },
];

const SPECS = {
  "60/70": [
    ["Penetration @ 25°C, 0.1 mm", "60 – 70", "ASTM D5"],
    ["Softening point, °C", "49 – 56", "ASTM D36"],
    ["Ductility @ 25°C, cm", "≥ 100", "ASTM D113"],
    ["Flash point (COC), °C", "≥ 232", "ASTM D92"],
    ["Solubility in TCE, %", "≥ 99.0", "ASTM D2042"],
  ],
  "80/100": [
    ["Penetration @ 25°C, 0.1 mm", "80 – 100", "ASTM D5"],
    ["Softening point, °C", "45 – 52", "ASTM D36"],
    ["Ductility @ 25°C, cm", "≥ 100", "ASTM D113"],
    ["Flash point (COC), °C", "≥ 225", "ASTM D92"],
    ["Solubility in TCE, %", "≥ 99.0", "ASTM D2042"],
  ],
};

const PACKAGING = [
  { name: "Bulk vessel", detail: "Heated bulk shipments for large infrastructure programmes" },
  { name: "Bitutainer", detail: "±24 MT per heated ISO container, door-to-door" },
  { name: "New steel drums", detail: "±180 kg drums, palletised for regional distribution" },
];

export default function Bitumen() {
  const [grade, setGrade] = useState("60/70");

  return (
    <div data-testid="bitumen-page">
      <section className="relative overflow-hidden">
        <img src="/assets/bitutainer.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/80 via-navy/90 to-navy" />
        <div className="relative mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
          <motion.p {...fadeUp} className="font-mono text-xs uppercase tracking-[0.35em] text-brand">Product Line 01</motion.p>
          <motion.h1 {...fadeUp} className="mt-4 text-3xl font-extrabold text-white sm:text-4xl lg:text-5xl">Bitumen</motion.h1>
          <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="mt-6 max-w-2xl text-base text-slate-300 md:text-lg">
            Paving-grade bitumen, cutbacks and emulsions for road construction and waterproofing — sourced from
            established refineries and delivered in bulk, bitutainers or new steel drums.
          </motion.p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20 md:px-12">
        <motion.h2 {...fadeUp} className="text-2xl font-bold text-white sm:text-3xl">Grades we trade</motion.h2>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {GRADES.map((g, i) => (
            <motion.div
              key={g.name}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.08 }}
              className="rounded-lg border border-navy-600 bg-navy-800 p-7 transition-colors duration-300 hover:border-brand/50"
              data-testid={`bitumen-grade-card-${i}`}
            >
              <g.icon className="h-8 w-8 text-brand" />
              <h3 className="mt-5 text-lg font-bold text-white">{g.name}</h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {g.items.map((it) => (
                  <span key={it} className="rounded-full border border-navy-600 px-3 py-1 font-mono text-xs text-brand">{it}</span>
                ))}
              </div>
              <p className="mt-4 text-sm leading-relaxed text-slate-400">{g.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="border-y border-navy-600/60 bg-navy-800/50" data-testid="bitumen-spec-section">
        <div className="mx-auto max-w-7xl px-6 py-20 md:px-12">
          <motion.h2 {...fadeUp} className="text-2xl font-bold text-white sm:text-3xl">Typical specifications</motion.h2>
          <div className="mt-8 flex gap-3">
            {Object.keys(SPECS).map((g) => (
              <button
                key={g}
                onClick={() => setGrade(g)}
                data-testid={`spec-tab-${g.replace("/", "-")}`}
                className={`rounded-md px-6 py-2 font-mono text-sm transition-colors duration-200 ${
                  grade === g ? "bg-brand text-navy" : "border border-navy-600 text-slate-300 hover:border-brand/60"
                }`}
              >
                {g}
              </button>
            ))}
          </div>
          <div className="mt-6 overflow-x-auto rounded-lg border border-navy-600" data-testid="bitumen-spec-table">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-navy-600 bg-navy-700 text-xs uppercase tracking-wider text-slate-400">
                  <th className="px-6 py-4 font-medium">Property</th>
                  <th className="px-6 py-4 font-medium">Specification</th>
                  <th className="px-6 py-4 font-medium">Test method</th>
                </tr>
              </thead>
              <tbody>
                {SPECS[grade].map(([prop, val, method]) => (
                  <tr key={prop} className="border-b border-navy-600/50 bg-navy-800 last:border-0">
                    <td className="px-6 py-4 text-slate-300">{prop}</td>
                    <td className="px-6 py-4 font-mono text-brand">{val}</td>
                    <td className="px-6 py-4 font-mono text-slate-500">{method}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs text-slate-500">
            Indicative values. A Certificate of Analysis accompanies every cargo; project-specific specifications
            can be matched on request.
          </p>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl items-center gap-14 px-6 py-20 md:px-12 lg:grid-cols-2">
        <motion.div {...fadeUp} className="overflow-hidden rounded-lg border border-navy-600">
          <img src="/assets/diesel-tank.jpg" alt="Storage tanks at a petroleum terminal" className="h-full w-full object-cover" />
        </motion.div>
        <motion.div {...fadeUp}>
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-brand">Packaging &amp; Logistics</p>
          <h2 className="mt-4 text-2xl font-bold text-white sm:text-3xl">Delivered the way your site needs it</h2>
          <div className="mt-8 space-y-4">
            {PACKAGING.map((p, i) => (
              <div key={p.name} className="flex items-start gap-4 rounded-lg border border-navy-600 bg-navy-800 p-5" data-testid={`packaging-${i}`}>
                <span className="mt-0.5 h-2 w-2 shrink-0 rounded-full bg-brand" />
                <div>
                  <h3 className="font-semibold text-white">{p.name}</h3>
                  <p className="mt-1 text-sm text-slate-400">{p.detail}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-9 flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid="bitumen-cta-quote"
              className="inline-flex items-center gap-2 rounded-md bg-brand px-7 py-3 text-sm font-semibold text-navy transition-transform duration-200 hover:scale-105 hover:shadow-[0_0_28px_rgba(0,208,132,0.4)]"
            >
              Request bitumen pricing <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="/specs/bitumen-spec-sheet.pdf"
              download
              data-testid="bitumen-spec-download"
              className="inline-flex items-center gap-2 rounded-md border border-navy-600 px-7 py-3 text-sm font-semibold text-slate-200 transition-colors duration-200 hover:border-brand/60 hover:text-brand"
            >
              Download spec sheet (PDF)
            </a>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
